/*
 * Copyright (c) 2023
 *
 * The source code and all documents related to the source code ("Material") are owned by
 * Frictionless Systems, LLC.
 *
 * The Material contains trade secrets, proprietary information, and confidential information.
 * No part of the Material may be used, copied, reproduced, modified, published, distributed, or
 * disclosed in any way without Frictionless Systems, LLC’s express prior written permission.
 *
 * The Material may be protected under patent, trademark, copyright, or trade secret laws.  No
 * license is granted to you by disclosure or delivery of the Materials.  Any license must be
 * express and approved by Frictionless Systems, LLC in writing.
 *
 * “Frictionless Systems” and “Impulse” are registered trademarks of Frictionless Systems, LLC.
 */

package systems.frictionless.impulse.browser.composable

import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import systems.frictionless.impulse.browser.composable.component.ContentContainer
import systems.frictionless.impulse.browser.composable.component.FullScreenErrorMessage
import systems.frictionless.impulse.browser.composable.component.LoadingIndicator
import systems.frictionless.impulse.browser.composable.component.SideNavigationSection
import systems.frictionless.impulse.browser.style.AppStyleSheet
import systems.frictionless.impulse.model.AuthenticatedUser
import systems.frictionless.impulse.rest.client.model.RestResponse
import systems.frictionless.impulse.rest.model.Assessment
import systems.frictionless.impulse.viewmodel.AssessmentViewModel
import systems.frictionless.impulse.viewmodel.NetworkViewModelState
import systems.frictionless.impulse.viewmodel.getNewestAssessment

@Composable
fun CreateAssessmentSection(
    assessmentViewModel: AssessmentViewModel,
    isViewLastResult: Boolean,
    onAssessmentCreated: () -> Unit
) {
    /*
     * A little icky; need to retain nullable state to pass along to the next state.
     * While we tested attaching this to the states, which eliminated the null safety issues, it didn't feel right
     * from an API design standpoint.
     */
    val (prevAssessmentList, setPrevAssessmentList) = remember { mutableStateOf<List<Assessment>?>(null) }

    ContentContainer(
        navigationSection = SideNavigationSection.Demographics,
        mainContainerCssClass = AppStyleSheet.createAssessment,
        mainContent = {
            when (val assessmentStateSnapshot = assessmentViewModel.state.collectAsState().value) {
                is AssessmentViewModel.State.ListAssessments -> {
                    ListSection(assessmentViewModel.authenticatedUser, assessmentStateSnapshot) {
                        setPrevAssessmentList(it)
                        assessmentViewModel.transition(isViewLastResult)
                    }
                }
                is AssessmentViewModel.State.GetMostRecentAssessmentStatus -> {
                    GetMostRecentSection(
                        assessmentViewModel.authenticatedUser,
                        prevAssessmentList?.getNewestAssessment()!!,
                        assessmentStateSnapshot
                    ) {
                        assessmentViewModel.transition(isViewLastResult)
                    }
                }
                is AssessmentViewModel.State.CreateAssessment -> {
                    CreateSection(assessmentViewModel.authenticatedUser, assessmentStateSnapshot) {
                        assessmentViewModel.transition(isViewLastResult)
                    }
                }
                is AssessmentViewModel.State.Done -> {
                    onAssessmentCreated()
                }
            }
        }
    )
}

@Composable
private fun ListSection(
    authenticatedUser: AuthenticatedUser,
    viewModel: AssessmentViewModel.State.ListAssessments,
    onComplete: (List<Assessment>) -> Unit
) {
    val composableScope = rememberCoroutineScope()

    val loadListOfAssessments = { viewModel.startListCheck(composableScope, authenticatedUser.authentication) }
    when (val state = viewModel.state.collectAsState().value) {
        is NetworkViewModelState.Idle -> {
            AssessmentProgress()
            loadListOfAssessments()
        }
        is NetworkViewModelState.Busy -> AssessmentProgress()
        is NetworkViewModelState.Failure -> Error(state.failure) { loadListOfAssessments() }
        is NetworkViewModelState.Complete -> onComplete(state.result)
    }
}

@Composable
private fun GetMostRecentSection(
    authenticatedUser: AuthenticatedUser,
    assessment: Assessment,
    viewModel: AssessmentViewModel.State.GetMostRecentAssessmentStatus,
    onComplete: (AssessmentViewModel.State.GetMostRecentAssessmentStatus.AssessmentWithState) -> Unit
) {
    val composableScope = rememberCoroutineScope()

    val getAssessmentStatus =
        { viewModel.getAssessmentStatus(composableScope, authenticatedUser.authentication, assessment) }
    when (val state = viewModel.state.collectAsState().value) {
        is NetworkViewModelState.Idle -> {
            AssessmentProgress()
            getAssessmentStatus()
        }
        is NetworkViewModelState.Busy -> AssessmentProgress()
        is NetworkViewModelState.Failure -> Error(state.failure) { getAssessmentStatus() }
        is NetworkViewModelState.Complete -> onComplete(state.result)
    }
}

@Composable
private fun CreateSection(
    authenticatedUser: AuthenticatedUser,
    viewModel: AssessmentViewModel.State.CreateAssessment,
    onComplete: () -> Unit
) {
    val composableScope = rememberCoroutineScope()
    val assessmentBuilder = remember { Assessment.Builder(Assessment.Type(Assessment.Type.IMPULSE)) }

    val createAssessment = { viewModel.create(composableScope, authenticatedUser.authentication, assessmentBuilder) }
    when (val state = viewModel.state.collectAsState().value) {
        is NetworkViewModelState.Idle -> {
            AssessmentProgress()
            createAssessment()
        }
        is NetworkViewModelState.Busy -> AssessmentProgress()
        is NetworkViewModelState.Failure -> Error(state.failure) { createAssessment() }
        is NetworkViewModelState.Complete -> {
            onComplete()
        }
    }
}

@Composable
private fun Error(
    failure: RestResponse.Failure<*>,
    retry: () -> Unit
) {
    FullScreenErrorMessage(
        "There was trouble connecting to the server to start the assessment.",
        failure,
        retry
    )
}

@Composable
private fun AssessmentProgress() {
    LoadingIndicator()
}
